import { client } from 'components/ApolloProvider'
import { loader } from 'graphql.macro'
import {
  Approval,
  Campaign,
  CampaignTarget,
  MediumUsage,
  OrganizationAttributes,
} from 'types/campaign'
import { MutationStateResponse, PaginationProps } from 'types/various'
import VariableUtils from 'utils/variable'
import { Agency } from 'types/agency'
import * as CAT from './types' // Campaign API types
import {
  CampaignDepartment,
  CampaignLocationsFilePublicResponse,
  CampaignLocationsFileResponse,
  CampaignPdfAudienceData,
  CreateCampaignSummaryPdfResponse,
  FetchPdfTaskStatusResponse,
  GetCampaignImpressionsVariables,
  UpdateMediaCriteriaSearchAdminVariables,
  UpdateMediaCriteriaSearchEmployeeVariables,
  UpdateMediaCriteriaSearchSupportVariables,
  UpdateMediaCriteriaSearchVariables,
} from './types'
import { GET_CAMPAIGN_DETAILS, GET_CAMPAIGN_DETAILS_PUBLIC } from './getCampaignDetails'
import { GET_CAMPAIGN_FOR_ACCEPTANCE_FIELDS } from './getCampaignForAcceptanceFields'
import { GET_CAMPAIGN_FOR_BASIC_FORM } from './getCampaignForBasicForm'
import { GET_CAMPAIGN_FOR_CREATIONS_FORM } from './getCampaignForCreationsForm'
import { GET_CAMPAIGN_FOR_DETAILS, GET_CAMPAIGN_FOR_DETAILS_PUBLIC } from './getCampaignForDetails'
import { GET_CAMPAIGN_FOR_DETAILS_FORM } from './getCampaignForDetailsForm'
import { GET_CAMPAIGN_IMPRESSIONS, GET_CAMPAIGN_IMPRESSIONS_PUBLIC } from './getCampaignImpressions'
import { GET_CAMPAIGNS_FOR_TABLE } from './getCampaignsForTable'
import { PAUSE_CAMPAIGN } from './pauseCampaign'
import { STOP_CAMPAIGN } from './stopCampaign'
import {
  GET_CAMPAIGN_FOR_EMISSION_SCHEDULE,
  GET_CAMPAIGN_FOR_EMISSION_SCHEDULE_PUBLIC,
} from './getCampaignForEmissionSchedule'
import { CREATE_SKETCH_CAMPAIGN } from './createSketchCampaign'
import { UPDATE_CAMPAIGN } from './updateCampaign'
import {
  UPDATE_MEDIA_CRITERIA_SEARCH,
  UPDATE_MEDIA_CRITERIA_SEARCH_ADMIN,
  UPDATE_MEDIA_CRITERIA_SEARCH_EMPLOYEE,
  UPDATE_MEDIA_CRITERIA_SEARCH_SUPPORT,
} from './updateMediaCriteriaSearch'
import { PUBLISH_CAMPAIGN } from './publishCampaign'
import { GET_CAMPAIGN_ESTIMATED_IMPRESSIONS } from './campaignEstimatedImpressions'
import { GET_CAMPAIGN_FILTERS } from './getCampaignFilters'
import { Filters } from 'pages/Campaign/CampaignTable/CampaignTableFilters'
import { CampaignReservation } from 'pages/Campaign/CampaignForm/Reservation'
import { RESERVE_CAMPAIGN } from './reserveCampaign'
import { UPDATE_CAMPAIGN_RESERVATION } from './updateCampaignReservation'
import { CONFIRM_CAMPAIGN_RESERVATION } from './confirmCampaignReservation'
import { CANCEL_CAMPAIGN_RESERVATION } from './cancelCampaignReservation'
import { RESTORE_CAMPAIGN_TO_SKETCH } from './restoreCampaignToSketch'
import { User } from 'types/user'
import { GET_CAMPAIGN_MEDIA_USAGE } from './getMediaUsage'
import { UPDATE_CAMPAIGN_MEDIA_USAGE } from './updateMediaUsage'
import { UPDATE_CAMPAIGN_ORGANIZATION_ATTRIBUTES } from './updateCampaignOrganizationAttributes'
import { FetchResult } from '@apollo/client'
import { GET_CAMPAIGN_LOCATION_FILE } from './getCampaignLocationsFile'
import { GET_CAMPAIGN_LOCATION_FILE_PUBLIC } from './getCampaignLocationsFilePublic'
import { UPDATE_CAMPAIGN_WITHOUT_SUPERVISORS } from './updateCampaignWithoutSupervisors'
import { SEND_CAMPAIGN_TO_ACCEPTANCE } from './sendCampaignToAcceptance'
import { Company } from '../companies/types'
import { GET_CAMPAIGN_IMPRESSIONS_BASED_ON_AUDIENCE } from './campaignImpressionsBasedOnAudience'
import { GET_CAMPAIGN_IMPRESSIONS_BASED_ON_FREQUENCY } from './campaignImpressionsBasedOnFrequency'
import { GET_CAMPAIGN_AUDIENCE_DATA } from './getCampaignAudienceData'
import { CREATE_CAMPAIGN_SUMMARY_PDF, FETCH_PDF_TASK_STATUS } from './createCampaignSummaryPdf'
import { CampaignImpressionResponse } from '../../pages/Campaign/CampaignDetails/CampaignDetailsChart/types'
import { GET_CAMPAIGN_SUGGESTED_AUDIENCE } from './campaignSuggestedAudience'
import { UPDATE_CAMPAIGN_VAST_REQUEST_MULTIPLIER } from './updateCampaignVastRequestMultiplier'
import { GET_CAMPAIGN_VAST_REQUEST_MULTIPLIER } from './getCampaignVastRequestMultiplier'
import { GET_DEPARTMENTS } from './getDepartments'
import { UPDATE_CAMPAIGN_CREATIONS } from './updateCampaignCreations'
import { VoohCampaignsFilters } from '../../pages/VoohCampaign/VoohCampaignTableFilters'
import { GET_CAMPAIGNS_BY_TAG } from './getCampaignsByTag'

const campaignApi = {
  getCampaignForDetails: (id: Campaign['id']): Promise<CAT.GetCampaignForDetailsAQR> =>
    client.query({
      query: GET_CAMPAIGN_FOR_DETAILS,
      variables: { id },
    }),

  getCampaignForDetailsPublic: (
    uuid: Campaign['uuid']
  ): Promise<CAT.GetCampaignForDetailsPublicAQR> =>
    client.query({
      query: GET_CAMPAIGN_FOR_DETAILS_PUBLIC,
      variables: { campaignUuid: uuid },
    }),

  getCampaignForAcceptanceFields: (
    id: Campaign['id']
  ): Promise<CAT.GetCampaignForAcceptanceFieldsAQR> =>
    client.query({
      query: GET_CAMPAIGN_FOR_ACCEPTANCE_FIELDS,
      variables: { id },
    }),

  getCampaignForBasicForm: (id: Campaign['id']): Promise<CAT.GetCampaignForBasicFormAQR> =>
    client.query({
      query: GET_CAMPAIGN_FOR_BASIC_FORM,
      variables: { id },
    }),

  getCampaignForDetailsForm: (id: Campaign['id']): Promise<CAT.GetCampaignForDetailsFormAQR> =>
    client.query({
      query: GET_CAMPAIGN_FOR_DETAILS_FORM,
      variables: { id },
    }),

  getCampaignForCreationsForm: (
    id: Campaign['id']
  ): Promise<CAT.GetCampaignForCreationsFormAQR> => {
    return client.query({
      query: GET_CAMPAIGN_FOR_CREATIONS_FORM,
      variables: { id },
    })
  },

  getCampaignFilters: (): Promise<CAT.GetCampaignFiltersAQR> =>
    client.query({
      query: GET_CAMPAIGN_FILTERS,
    }),

  getCampaignsForTable: (
    variables: PaginationProps & { filters: Filters | VoohCampaignsFilters }
  ): Promise<CAT.GetCampaignsForTableAQR> => {
    const { startDateFrom, startDateTo, endDateFrom, endDateTo, text, ...rest } = variables.filters
    const { reservationTillFrom, reservationTillTo, ...filters } = rest as Filters

    return client.query({
      query: GET_CAMPAIGNS_FOR_TABLE,
      variables: {
        ...variables,
        filters: {
          ...filters,
          startDateFrom: startDateFrom || null,
          startDateTo: startDateTo || null,
          endDateFrom: endDateFrom || null,
          endDateTo: endDateTo || null,
          reservationTillFrom: reservationTillFrom || null,
          reservationTillTo: reservationTillTo || null,
          text: text || null,
        },
      },
    })
  },

  getCampaignForEmissionSchedule: (
    id: Campaign['id']
  ): Promise<CAT.GetCampaignForEmissionScheduleAQR> =>
    client.query({
      query: GET_CAMPAIGN_FOR_EMISSION_SCHEDULE,
      variables: { id },
    }),

  getCampaignForEmissionSchedulePublic: (
    uuid: Campaign['uuid']
  ): Promise<CAT.GetCampaignForEmissionSchedulePublicAQR> =>
    client.query({
      query: GET_CAMPAIGN_FOR_EMISSION_SCHEDULE_PUBLIC,
      variables: { campaignUuid: uuid },
    }),

  getCampaignDetails: (id: Campaign['id']): Promise<CAT.GetCampaignDetailsAQR> =>
    client.query({
      query: GET_CAMPAIGN_DETAILS,
      variables: { id },
    }),

  getCampaignDetailsPublic: (uuid: Campaign['uuid']): Promise<CAT.GetCampaignDetailsPublicAQR> =>
    client.query({
      query: GET_CAMPAIGN_DETAILS_PUBLIC,
      variables: { campaignUuid: uuid },
    }),

  createCampaignSummaryPdf: (
    uuid: Campaign['uuid']
  ): Promise<FetchResult<{ createCampaignSummaryPdf: CreateCampaignSummaryPdfResponse }>> =>
    client.mutate({
      mutation: CREATE_CAMPAIGN_SUMMARY_PDF,
      variables: { campaignUuid: uuid },
    }),

  getCampaignVastRequestMultiplier: (
    campaignId: Campaign['id']
  ): Promise<
    FetchResult<{
      campaignVastRequestMultipliers: { multiplier: number }
    }>
  > =>
    client.query({
      query: GET_CAMPAIGN_VAST_REQUEST_MULTIPLIER,
      variables: {
        campaignId,
      },
    }),

  updateCampaignVastRequestMultiplier: (
    campaignId: Campaign['id'],
    multiplier: number
  ): Promise<FetchResult<{ createCampaignSummaryPdf: CreateCampaignSummaryPdfResponse }>> =>
    client.mutate({
      mutation: UPDATE_CAMPAIGN_VAST_REQUEST_MULTIPLIER,
      variables: { campaignId, multiplier },
    }),

  updateCampaignCreations: (
    campaignId: Campaign['id'],
    creationContributionType: Campaign['creationContributionType']
  ): Promise<FetchResult<{ updateCampaignCreations: unknown }>> =>
    client.mutate({
      mutation: UPDATE_CAMPAIGN_CREATIONS,
      variables: {
        id: campaignId,
        creationContributionType,
      },
    }),

  fetchPdfTaskStatus: (jobId: string): Promise<FetchResult<{ task: FetchPdfTaskStatusResponse }>> =>
    client.query({
      query: FETCH_PDF_TASK_STATUS,
      variables: { jobId },
    }),

  fetchDepartments: (term: string): Promise<FetchResult<{ departments: CampaignDepartment[] }>> =>
    client.query({
      query: GET_DEPARTMENTS,
      variables: { term },
    }),

  createSketchCampaign: (v: {
    agencyId: Agency['id'] | null
    name: Campaign['name']
    briefName: Campaign['briefName']
    startDate: Campaign['startDate'] | null
    endDate: Campaign['endDate'] | null
    agencyDiscount: string | null
    campaignTagList: string[] | null
  }): Promise<CAT.CreateSketchCampaignFR> =>
    client.mutate({
      mutation: CREATE_SKETCH_CAMPAIGN,
      variables: {
        agencyId: v.agencyId || null,
        name: v.name,
        briefName: v.briefName,
        startDate: v.startDate || null,
        endDate: v.endDate || null,
        agencyDiscount: v.agencyDiscount || null,
        campaignTagList: v.campaignTagList || null,
      },
    }),

  updateCampaign: (payload: {
    id: Campaign['id']
    agencyId: Agency['id'] | null
    advertiserId: Company['id'] | null
    name: Campaign['name'] | null
    briefName: Campaign['briefName'] | null
    startDate: Campaign['startDate'] | null
    endDate: Campaign['endDate'] | null
    supervisors: User['id'][]
    commercialSupervisorId: User['id'] | null
    ordererId: Company['id'] | null
    departmentId: CampaignDepartment['id'] | null
    broadsignTotalOutdoor: Campaign['broadsignTotalOutdoor'] | null
    agencyDiscount: Campaign['agencyDiscount'] | null
    campaignTagList: string[] | null
  }): Promise<CAT.UpdateCampaignFR> =>
    client.mutate({
      mutation: UPDATE_CAMPAIGN,
      variables: {
        id: payload.id,
        agencyId: payload.agencyId || null,
        advertiserId: payload.advertiserId || null,
        name: payload.name || null,
        briefName: payload.briefName || null,
        startDate: payload.startDate || null,
        endDate: payload.endDate || null,
        supervisors: payload.supervisors,
        commercialSupervisorId: payload.commercialSupervisorId || null,
        ordererId: payload.ordererId || null,
        departmentId: payload.departmentId || null,
        broadsignTotalOutdoor: payload.broadsignTotalOutdoor || null,
        agencyDiscount: payload.agencyDiscount || null,
        campaignTagList: payload.campaignTagList || null,
      },
    }),

  updateCampaignWithoutSupervisors: (payload: {
    id: Campaign['id']
    agencyId: Agency['id'] | null
    advertiserId: Company['id'] | null
    name: Campaign['name'] | null
    briefName: Campaign['briefName'] | null
    startDate: Campaign['startDate'] | null
    endDate: Campaign['endDate'] | null
    commercialSupervisorId: User['id'] | null
    ordererId: Company['id'] | null
    departmentId: CampaignDepartment['id'] | null
    broadsignTotalOutdoor: Campaign['broadsignTotalOutdoor'] | null
    agencyDiscount: Campaign['agencyDiscount'] | null
  }): Promise<CAT.UpdateCampaignFR> =>
    client.mutate({
      mutation: UPDATE_CAMPAIGN_WITHOUT_SUPERVISORS,
      variables: {
        id: payload.id,
        agencyId: payload.agencyId || null,
        advertiserId: payload.advertiserId || null,
        name: payload.name || null,
        briefName: payload.briefName || null,
        startDate: payload.startDate || null,
        endDate: payload.endDate || null,
        commercialSupervisorId: payload.commercialSupervisorId || null,
        ordererId: payload.ordererId || null,
        departmentId: payload.departmentId || null,
        broadsignTotalOutdoor: payload.broadsignTotalOutdoor || null,
        agencyDiscount: payload.agencyDiscount || null,
      },
    }),

  updateMediaCriteriaSearch: (
    variables: UpdateMediaCriteriaSearchVariables
  ): Promise<CAT.UpdateMediaCriteriaSearchFR> => {
    const {
      target,
      budget: rawBudget,
      emissions: rawImpressions,
      audience: rawAudience,
      frequency: rawFrequency,
      intensityModifier: rawIntensityModifier,
    } = variables
    const budget =
      rawBudget && target === CampaignTarget.BUDGET
        ? VariableUtils.numberToGqlFloat(rawBudget)
        : null
    const emissions =
      rawImpressions && target === CampaignTarget.IMPRESSIONS
        ? VariableUtils.numberToGqlInt(rawImpressions)
        : null
    const audience =
      rawAudience && target === CampaignTarget.AUDIENCE
        ? VariableUtils.numberToGqlInt(rawAudience)
        : null
    const frequency =
      rawFrequency && target === CampaignTarget.FREQUENCY
        ? VariableUtils.numberToGqlInt(rawFrequency)
        : null
    const intensityModifier =
      target === CampaignTarget.FREQUENCY &&
      rawIntensityModifier !== null &&
      rawIntensityModifier !== undefined &&
      `${rawIntensityModifier}` !== ''
        ? VariableUtils.numberToGqlFloat(Number(rawIntensityModifier))
        : null

    return client.mutate({
      mutation: UPDATE_MEDIA_CRITERIA_SEARCH,
      variables: {
        ...variables,
        budget,
        audience,
        frequency,
        intensityModifier,
        impressions: emissions,
        priority: VariableUtils.numberToGqlInt(variables.priority),
      },
    })
  },

  updateMediaCriteriaSearchEmployee: (
    variables: UpdateMediaCriteriaSearchEmployeeVariables
  ): Promise<CAT.UpdateMediaCriteriaSearchFR> => {
    const {
      target,
      budget: rawBudget,
      emissions: rawImpressions,
      frequency: rawFrequency,
      intensityModifier: rawIntensityModifier,
    } = variables
    const budget =
      rawBudget && target === CampaignTarget.BUDGET
        ? VariableUtils.numberToGqlFloat(rawBudget)
        : null
    const emissions =
      rawImpressions && target === CampaignTarget.IMPRESSIONS
        ? VariableUtils.numberToGqlInt(rawImpressions)
        : null
    const frequency =
      rawFrequency && target === CampaignTarget.FREQUENCY
        ? VariableUtils.numberToGqlInt(rawFrequency)
        : null
    const intensityModifier =
      target === CampaignTarget.FREQUENCY &&
      rawIntensityModifier !== null &&
      rawIntensityModifier !== undefined &&
      `${rawIntensityModifier}` !== ''
        ? VariableUtils.numberToGqlFloat(Number(rawIntensityModifier))
        : null

    return client.mutate({
      mutation: UPDATE_MEDIA_CRITERIA_SEARCH_EMPLOYEE,
      variables: {
        ...variables,
        budget,
        frequency,
        intensityModifier,
        impressions: emissions,
        priority: VariableUtils.numberToGqlInt(variables.priority),
      },
    })
  },

  updateMediaCriteriaSearchAdmin: (
    variables: UpdateMediaCriteriaSearchAdminVariables
  ): Promise<CAT.UpdateMediaCriteriaSearchFR> => {
    const {
      target,
      budget: rawBudget,
      emissions: rawImpressions,
      audience: rawAudience,
      frequency: rawFrequency,
      intensityModifier: rawIntensityModifier,
    } = variables
    const budget =
      rawBudget && target === CampaignTarget.BUDGET
        ? VariableUtils.numberToGqlFloat(rawBudget)
        : null
    const emissions =
      rawImpressions && target === CampaignTarget.IMPRESSIONS
        ? VariableUtils.numberToGqlInt(rawImpressions)
        : null
    const audience =
      rawAudience && target === CampaignTarget.AUDIENCE
        ? VariableUtils.numberToGqlInt(rawAudience)
        : null
    const frequency =
      rawFrequency && target === CampaignTarget.FREQUENCY
        ? VariableUtils.numberToGqlInt(rawFrequency)
        : null
    const intensityModifier =
      target === CampaignTarget.FREQUENCY &&
      rawIntensityModifier !== null &&
      rawIntensityModifier !== undefined &&
      `${rawIntensityModifier}` !== ''
        ? VariableUtils.numberToGqlFloat(Number(rawIntensityModifier))
        : null

    const guaranteedImpressions =
      target === CampaignTarget.AUDIENCE && variables.guaranteedImpressions
        ? VariableUtils.numberToGqlInt(variables.guaranteedImpressions)
        : null

    return client.mutate({
      mutation: UPDATE_MEDIA_CRITERIA_SEARCH_ADMIN,
      variables: {
        ...variables,
        budget,
        hiddenImpressions: VariableUtils.numberToGqlInt(variables.hiddenImpressions || 0),
        impressions: emissions,
        priority: VariableUtils.numberToGqlInt(variables.priority),
        audience: audience,
        frequency,
        intensityModifier,
        hiddenAudience: VariableUtils.numberToGqlInt(variables.hiddenAudience || 0),
        guaranteedImpressions,
      },
    })
  },

  updateMediaCriteriaSearchSupport: (
    variables: UpdateMediaCriteriaSearchSupportVariables
  ): Promise<CAT.UpdateMediaCriteriaSearchFR> => {
    const {
      target,
      budget: rawBudget,
      emissions: rawImpressions,
      frequency: rawFrequency,
      intensityModifier: rawIntensityModifier,
    } = variables
    const budget =
      rawBudget && target === CampaignTarget.BUDGET
        ? VariableUtils.numberToGqlFloat(rawBudget)
        : null
    const emissions =
      rawImpressions && target === CampaignTarget.IMPRESSIONS
        ? VariableUtils.numberToGqlInt(rawImpressions)
        : null
    const frequency =
      rawFrequency && target === CampaignTarget.FREQUENCY
        ? VariableUtils.numberToGqlInt(rawFrequency)
        : null
    const intensityModifier =
      target === CampaignTarget.FREQUENCY &&
      rawIntensityModifier !== null &&
      rawIntensityModifier !== undefined &&
      `${rawIntensityModifier}` !== ''
        ? VariableUtils.numberToGqlFloat(Number(rawIntensityModifier))
        : null

    return client.mutate({
      mutation: UPDATE_MEDIA_CRITERIA_SEARCH_SUPPORT,
      variables: {
        ...variables,
        budget,
        frequency,
        intensityModifier,
        impressions: emissions,
        priority: VariableUtils.numberToGqlInt(variables.priority),
      },
    })
  },

  updateApproval: (variables: {
    approvalId: Approval['id']
    approved: Approval['approved']
  }): Promise<FetchResult<{ updateApproval: MutationStateResponse }>> =>
    client.mutate({
      mutation: loader('./updateApproval.gql'),
      variables,
    }),

  getCampaignImpressions: (
    variables: GetCampaignImpressionsVariables
  ): Promise<FetchResult<{ campaignImpressions: { nodes: CampaignImpressionResponse[] } }>> =>
    client.query({
      query: GET_CAMPAIGN_IMPRESSIONS,
      variables,
    }),

  getCampaignImpressionsPublic: (
    variables: GetCampaignImpressionsVariables
  ): Promise<
    FetchResult<{
      campaignReportPublic: { campaignImpressions: { nodes: CampaignImpressionResponse[] } }
    }>
  > =>
    client.query({
      query: GET_CAMPAIGN_IMPRESSIONS_PUBLIC,
      variables,
    }),

  acceptCampaign: (variables: { id: Campaign['id'] }): Promise<CAT.acceptCampaignFR> =>
    client.mutate({
      mutation: loader('./acceptCampaign.gql'),
      variables,
    }),

  rejectCampaign: (variables: { id: Campaign['id'] }): Promise<CAT.rejectCampaignFR> =>
    client.mutate({
      mutation: loader('./rejectCampaign.gql'),
      variables,
    }),

  copyCampaign: (variables: {
    id: Campaign['id']
  }): Promise<
    FetchResult<{ copyCampaign: MutationStateResponse & { campaign: { id: string } } }>
  > =>
    client.mutate({
      mutation: loader('./copyCampaign.graphql'),
      variables,
    }),

  deleteCampaign: (
    id: Campaign['id']
  ): Promise<FetchResult<{ destroyCampaign: MutationStateResponse }>> =>
    client.mutate({
      mutation: loader('./destroyCampaign.gql'),
      variables: { id },
    }),

  pauseCampaign: (id: Campaign['id']): Promise<CAT.PauseCampaignFR> =>
    client.mutate({
      mutation: PAUSE_CAMPAIGN,
      variables: { id },
    }),

  stopCampaign: (id: Campaign['id']): Promise<CAT.StopCampaignFR> =>
    client.mutate({
      mutation: STOP_CAMPAIGN,
      variables: { id },
    }),

  publishCampaign: (variables: { campaignId: Campaign['id'] }): Promise<CAT.PublishCampaignFR> =>
    client.mutate({
      mutation: PUBLISH_CAMPAIGN,
      variables,
    }),

  getCampaignEstimatedImpressions: (
    campaignId: Campaign['id'],
    experimental: boolean
  ): Promise<CAT.GetCampaignEstimatedImpressionsQR> =>
    client.query({
      query: GET_CAMPAIGN_ESTIMATED_IMPRESSIONS,
      variables: { campaignId, experimental },
    }),

  getCampaignImpressionsBasedOnAudience: (
    campaignId: Campaign['id']
  ): Promise<CAT.GetCampaignImpressionsBasedOnAudience> =>
    client.query({
      query: GET_CAMPAIGN_IMPRESSIONS_BASED_ON_AUDIENCE,
      variables: { campaignId },
    }),

  getCampaignImpressionsBasedOnFrequency: (
    campaignId: Campaign['id']
  ): Promise<CAT.GetCampaignImpressionsBasedOnFrequency> =>
    client.query({
      query: GET_CAMPAIGN_IMPRESSIONS_BASED_ON_FREQUENCY,
      variables: { campaignId },
    }),

  getCampaignSuggestedAudience: (
    campaignId: Campaign['id']
  ): Promise<
    FetchResult<{
      campaignSuggestedAudience: {
        errors: string[]
        suggestedAudience: number
        suggestedImpressions: number
      }
    }>
  > =>
    client.query({
      query: GET_CAMPAIGN_SUGGESTED_AUDIENCE,
      variables: { campaignId },
    }),

  reserveCampaign: (variables: CampaignReservation): Promise<CAT.ReserveCampaignFR> =>
    client.mutate({
      mutation: RESERVE_CAMPAIGN,
      variables,
    }),

  updateCampaignReservation: (
    variables: CampaignReservation
  ): Promise<CAT.UpdateCampaignReservationFR> =>
    client.mutate({
      mutation: UPDATE_CAMPAIGN_RESERVATION,
      variables,
    }),

  confirmCampaignReservation: (
    variables: Pick<CampaignReservation, 'id'>
  ): Promise<CAT.ConfirmCampaignReservationFR> =>
    client.mutate({
      mutation: CONFIRM_CAMPAIGN_RESERVATION,
      variables,
    }),

  cancelCampaignReservation: (
    variables: Pick<CampaignReservation, 'id'>
  ): Promise<CAT.CancelCampaignReservationFR> =>
    client.mutate({
      mutation: CANCEL_CAMPAIGN_RESERVATION,
      variables,
    }),

  restoreCampaignToSketch: (id: Campaign['id']): Promise<CAT.RestoreCampaignToSketchFR> =>
    client.mutate({
      mutation: RESTORE_CAMPAIGN_TO_SKETCH,
      variables: { id },
    }),

  sendCampaignToAcceptance: (
    variables: Pick<CampaignReservation, 'id'>
  ): Promise<CAT.SendCampaignToAcceptanceFR> =>
    client.mutate({
      mutation: SEND_CAMPAIGN_TO_ACCEPTANCE,
      variables,
    }),

  getMediaUsage: (id: Campaign['id']): Promise<CAT.GetCampaignMediaAQR> =>
    client.query({
      query: GET_CAMPAIGN_MEDIA_USAGE,
      variables: { id },
    }),

  updateMediaUsage: (variables: {
    campaignId: Campaign['id']
    media: MediumUsage[]
  }): Promise<CAT.UpdateCampaignMediaUsageFR> => {
    return client.mutate({
      mutation: UPDATE_CAMPAIGN_MEDIA_USAGE,
      variables,
    })
  },

  updateCampaignOrganizationAttributes: (
    campaignId: Campaign['id'],
    organizationAttributes: OrganizationAttributes
  ): Promise<FetchResult<MutationStateResponse>> => {
    return client.mutate({
      mutation: UPDATE_CAMPAIGN_ORGANIZATION_ATTRIBUTES,
      variables: {
        campaignId,
        organizationAttribute: organizationAttributes,
      },
    })
  },

  fetchCampaignLocationsFile: (
    campaignId: Campaign['id']
  ): Promise<CampaignLocationsFileResponse> => {
    return client.query({
      query: GET_CAMPAIGN_LOCATION_FILE,
      variables: { campaignId },
    })
  },

  fetchCampaignPdfAudienceData: (
    campaignId: Campaign['id']
  ): Promise<FetchResult<{ campaignPdfAudienceData: CampaignPdfAudienceData }>> => {
    return client.query({
      query: GET_CAMPAIGN_AUDIENCE_DATA,
      variables: { campaignId },
    })
  },

  fetchCampaignLocationsFilePublic: (
    uuid: Campaign['uuid']
  ): Promise<CampaignLocationsFilePublicResponse> => {
    return client.query({
      query: GET_CAMPAIGN_LOCATION_FILE_PUBLIC,
      variables: { campaignUuid: uuid },
    })
  },

  getCampaignsByTag: (tag: string): Promise<CAT.GetCampaignsByTagAQR> =>
    client.query({
      query: GET_CAMPAIGNS_BY_TAG,
      variables: { tag },
    }),
}

export default campaignApi
